import mergeObjects from '../../../utils/mergeObjects';
import buildFormConfirmationData from './utils/buildFormConfirmationData';
import mapVehiclePerformance from './utils/mapVehiclePerformance';
import mapVehicleTechnology from './utils/mapVehicleTechnology';
import getFormProps from './utils/getFormProps';
import vinrealSelectorVdp from './utils/vinrealSelectorVdp';
import vinrealSrp from './utils/vinrealSrp';
import determineLinkType from './utils/determineLinkType';
import getLinkGenericPayload from './utils/getLinkGenericPayload';
import excludePagesFromDefaultLinkGeneric from './utils/excludePagesFromDefaultLinkGeneric';

const pageAlias = window.DDC.dataLayer.page.pageInfo.pageName;

const handleSocialClicks = ({ eventData }) => {
	const payload = {
		onclick: {}
	};

	const socialNetwork = eventData?.service?.toLowerCase?.();

	// use custom values if sharing from blog post
	if (
		window.DDC.dataLayer.page.pageInfo.pageName.includes('BLOG_POST_DETAIL')
	) {
		payload.onclick.onclick = `about us:blog:{pageTitle}:content:${socialNetwork}`;
		payload.onclick.onclickLinkName =
			'about us:blog:{pageTitle}:content:action';
	} else {
		payload.onclick.onclick = `share this:${socialNetwork}`;
		payload.onclick.onclickLinkName = 'share this';
	}

	return {
		directCall: 'global-exit',
		payload
	};
};

export default {
	events: {
		'ddc.wstagging.first': [
			{
				payload: ({
					mapping: {
						fdRegion: fordRegion,
						fdafRegionCode: fdafCode,
						paCode,
						pagealias: {
							event,
							page = {},
							page: {
								pageName: pageNameMapping,
								pageNameNoVehicle,
								vinreal
							} = {},
							vehicle
						} = {},
						form: formMapping = {}
					} = {}
				}) => {
					const {
						confirmation: { trackingId, trackingTags } = {},
						page: {
							attributes: { locale = '' } = {},
							pageInfo: { isConfirm = false, pageName = '' } = {}
						},
						site: { franchises } = {}
					} = window?.DDC?.dataLayer || {};

					const { isMobile, isTablet } = window?.DDC?.client || {};

					let deviceType = 'pc';

					if (isMobile) {
						deviceType = 'mobile';
					} else if (isTablet) {
						deviceType = 'tablet';
					}

					let userLanguage = 'eng';

					if (locale.startsWith('fr_')) {
						userLanguage = 'fre';
					} else if (locale.startsWith('es_')) {
						userLanguage = 'esp';
					}

					let client = franchises[0] || '';
					const isFord = franchises.includes('ford');
					const isLincoln = franchises.includes('lincoln');

					if (isFord && isLincoln) {
						client = 'ford-lincoln';
					}

					let payload = {
						...(event && { event }),
						page: {
							pageName: `dc:custom:${pageName
								.replace(/_/g, ' ')
								.toLowerCase()}`,
							siteSection: 'custom',
							hierarchy: 'custom',
							userLanguage,
							client,
							site: 'dc:ddc:us',
							radUIVersion: `ui:rad:${deviceType}`,
							variantName: 'global-load',
							...page,
							...(pageNameMapping && {
								pageName: `dc:${pageNameMapping}`
							}),
							...(pageNameNoVehicle && {
								pageNameNoVehicle: `dc:${pageNameNoVehicle}`
							}),
							...(vinreal && {
								vinreal:
									vinreal === 'vinrealsrp'
										? vinrealSrp()
										: vinrealSelectorVdp('vdp')
							})
						},
						user: {
							paCode,
							fordRegion,
							fdafCode
						},
						...(vehicle && { vehicle })
					};

					let formProps = {};
					if (isConfirm) {
						formProps = buildFormConfirmationData({
							formMapping,
							// since trackingTags sounds like it could be multiple strings
							// perhaps we should split this on a space and check all the strings
							// maybe that should be done in buildFormConfirmationData though
							// i don't think we use it like that yet though
							formTrackingId: trackingTags || trackingId
						});
						payload = mergeObjects(payload, formProps, true);
					}

					/**
					 * set pageNameNoVehicle before vehicle nameplate is appended to pageName
					 * only when pageNameNoVehicle is not coming from formProps
					 */
					if (!formProps?.page?.pageNameNoVehicle) {
						payload.page.pageNameNoVehicle = `${
							pageNameNoVehicle || payload.page.pageName
						}`;

						// IDEA/TODO: We could update all the mappings to just have pageNameNoVehicle
						// and then we could just add the nameplate to pageName
						//
						// strip of nameplate from pageNameNoVehicle
						// only when nameplate exists
						if (payload?.vehicle?.nameplate) {
							payload.page.pageNameNoVehicle =
								payload.page.pageNameNoVehicle.replace(
									`:${payload.vehicle.nameplate}`,
									''
								);
						}
					}

					if (
						payload?.page?.pageNameNoVehicle &&
						!payload.page.pageNameNoVehicle.startsWith('dc:')
					) {
						payload.page.pageNameNoVehicle = `dc:${payload.page.pageNameNoVehicle}`;
					}

					return payload;
				}
			}
		],
		'ddc.form.paymentCalcInit': [
			{
				// v9.widgets.calculator.payment.v1 used to be on VDPs which is why we need to test it with a vehicle
				// we could clean up the test and event mapping if we are sure this form is never going to be on a VDP again
				// https://onenr.io/0ERPJeeqkQW
				payload: ({ mapping }) => {
					const onclickLinkName =
						'{inventoryType}:search inventory:calculate payment';

					return {
						directCall: 'vehicle-onclick',
						payload: {
							onclick: {
								onclick: 'calculate payment',
								onclickLinkName: mapping.templateStrings
									.nameplate
									? `${onclickLinkName}:${mapping.templateStrings.nameplate}`
									: `${onclickLinkName}`
							}
						}
					};
				}
			}
		],
		'ddc.link.generic': [
			{
				logic: ({ element, eventData }) =>
					// add pages to the below list to avoid getting caught by logic in the legacy solution
					!excludePagesFromDefaultLinkGeneric.includes(
						eventData?.pageName
					) && determineLinkType(element) !== 'generic',
				payload: ({ element, eventData, mapping }) =>
					getLinkGenericPayload({ element, eventData, mapping })
			},
			{
				logic: {
					'eventData.attributes.data-modified-by':
						'forddirect-add-model-e-cta-to-vdp'
				},
				payload: {
					directCall: 'referral-exit',
					payload: ({
						eventData: {
							vehicle: { make, model }
						}
					}) => ({
						onclick: {
							onclick: `{inventoryType}:si:{vehiclePageType}${
								window.DDC.dataLayer.page.pageInfo.isVdp
									? ''
									: ':content'
							}:model e buy now`,
							// ideally, there should be a colon between {client} and {inventoryType},
							// that seems to be an edge case in functions 'replaceTemplateStrings' and 'mergeObjects' in the utils
							onclickLinkName: `{client}{inventoryType}:si:{vehiclePageType}:content:action:${make} ${model}`,
							referredTo: '[[eventData.url]]'
						},
						event: {
							action: 'brand referral'
						}
					})
				}
			},
			{
				logic: {
					isTelephonicUrl: true
				},
				payload: {
					directCall: 'click-to-call',
					payload: {
						onclick: {
							onclick: 'help:click to call',
							onclickLinkName: '{client}help:click to call'
						},
						event: {
							action: 'click to call'
						}
					}
				}
			},
			{
				logic: ({ eventData }) =>
					eventData.pageName === 'PICKUP_AND_DELIVERY' &&
					eventData.attributes['data-form-id'] === 'service1',
				payload: {
					directCall: 'global-overlay',
					payload: {
						page: {
							pageName:
								'{client}ownership:pickup and delivery:contact us',
							pageNameNoVehicle:
								'{client}ownership:pickup and delivery:contact us',
							hierarchy: 'ownership:contact us'
						}
					}
				}
			}
		],
		'ddc.form.initiation': [
			{
				payload: ({
					eventData: {
						trackingId,
						trackingTags,
						vehicle: { make, model, modelYear } = {}
					} = {},
					mapping: { form: formMapping = {} } = {}
				}) => {
					const { directCall = 'global-overlay', ...formProps } =
						getFormProps({
							formMapping,
							formTrackingId: trackingTags || trackingId
						}) || {};

					if (Object.keys(formProps).length) {
						const payload = mergeObjects(
							window.pageLoadDigitaldata,
							formProps,
							true
						);

						if (
							window.DDC.dataLayer.page.pageInfo.isVlp &&
							make &&
							model &&
							modelYear
						) {
							payload.vehicle = payload.vehicle || {};
							payload.vehicle.nameplate = `${make} ${model}`;
							payload.vehicle.modelYear = `${modelYear}`;
						}

						if (!payload.page.pageNameNoVehicle) {
							payload.page.pageNameNoVehicle =
								payload.page.pageName;
						}
						if (payload?.vehicle?.nameplate) {
							payload.page.pageName += `:${payload.vehicle.nameplate}`;
						}

						return {
							directCall,
							payload
						};
					}

					return {};
				}
			}
		],
		'ddc.form.confirmation': [
			{
				logic: ({
					eventData: { trackingId, trackingTags },
					mapping: { form: formMapping = {} }
				}) => {
					if (window.DDC.dataLayer.page.pageInfo.isConfirm) {
						return false;
					}
					const formProps = buildFormConfirmationData({
						formMapping,
						formTrackingId: trackingTags || trackingId
					});
					return typeof formProps === 'object';
				},
				payload: ({
					eventData: { trackingId, trackingTags, vehicle },
					mapping: { form: formMapping = {} }
				}) => {
					const formProps = buildFormConfirmationData({
						formMapping,
						formTrackingId: trackingTags || trackingId
					});
					formProps.page = formProps.page || {};
					formProps.page.pageNameNoVehicle = formProps.page.pageName;
					if (vehicle) {
						const bodyStyle = vehicle.bodyStyle.toLowerCase();
						const nameplate =
							vehicle.make &&
							vehicle.model &&
							`${vehicle.make} ${vehicle.model}`;
						const vehiclePerformance = mapVehiclePerformance(
							vehicle.trim
						);
						const vehicleTechnology = mapVehicleTechnology(
							vehicle.model
						);
						formProps.vehicle = formProps.vehicle || {};
						formProps.vehicle.modelYear = vehicle.modelYear;
						formProps.vehicle.price = `${
							vehicle.internetPrice || vehicle.msrp
						}`;
						formProps.vehicle.bodyModelTrim = `${vehicleTechnology}:${bodyStyle}:${vehicle.trim}:${vehiclePerformance}`;

						if (vehicle.engine && vehicle.transmission) {
							formProps.vehicle.engineTrans = `${vehicle.engine}:${vehicle.transmission}`;
						}

						if (vehicle.exteriorColor && vehicle.interiorColor) {
							formProps.vehicle.extIntColorCode = `${vehicle.exteriorColor}:${vehicle.interiorColor}`;
						}

						if (nameplate) {
							formProps.vehicle.nameplate = nameplate;
							formProps.page.pageName += `:${nameplate}`;
						}

						// update vehicle information that may be requested in hierarchy
						if (typeof formProps.page.hierarchy === 'string') {
							formProps.page.hierarchy =
								formProps.page.hierarchy.replace(
									/({bodyStyle})/gi,
									bodyStyle
								);
							formProps.page.hierarchy =
								formProps.page.hierarchy.replace(
									/({nameplate})/gi,
									nameplate || ''
								);
							formProps.page.hierarchy =
								formProps.page.hierarchy.replace(
									/({modelYear})/gi,
									vehicle.modelYear
								);
						}
					}
					const results = {
						directCall: 'global-overlay',
						payload: {
							...formProps
						}
					};
					return results;
				}
			}
		],
		'ddc.link.socialHeaderClick': [
			{
				payload: ({ eventData }) => {
					return handleSocialClicks({ eventData });
				}
			}
		],
		'ddc.link.socialShare': [
			{
				payload: ({ eventData }) => {
					return handleSocialClicks({ eventData });
				}
			}
		],
		'ddc.video.play': [
			{
				logic: () => pageAlias.includes('COMMODITY_PAGE_BRAKES'),
				payload: {
					payload: {
						onclick: {
							onclick: 'brake service:content:video play',
							onclickLinkName:
								'service:brake service:content:action'
						}
					}
				}
			},
			{
				logic: () => pageAlias.includes('COMMODITY_PAGE_OIL'),
				payload: {
					payload: {
						onclick: {
							onclick: 'oil change:content:video play',
							onclickLinkName: 'service:oil change:content:action'
						}
					}
				}
			},
			{
				logic: () => pageAlias.includes('COMMODITY_PAGE_TIRES'),
				payload: {
					payload: {
						onclick: {
							onclick: 'tire care:content:video play',
							onclickLinkName: 'service:tire care:content:action'
						}
					}
				}
			},
			{
				logic: () => pageAlias.includes('WHY_BUY'),
				payload: {
					payload: {
						onclick: {
							onclick: 'research models:content:video play',
							onclickLinkName:
								'research models:why buy:content:action'
						},
						page: {
							hierarchy: 'research models'
						}
					}
				}
			},
			{
				logic: () => pageAlias === 'SITEBUILDER_FORD_MOBILE_SERVICE_1',
				payload: {
					payload: {
						onclick: {
							onclick: 'service:mobileservice:content:video play',
							onclickLinkName:
								'service:mobileservice:content:action'
						}
					}
				}
			},
			{
				logic: () => pageAlias.includes('PICKUP_AND_DELIVERY'),
				payload: {
					payload: {
						onclick: {
							onclick: 'pickup and delivery:content:video play',
							onclickLinkName:
								'pickup and delivery:content:action'
						}
					}
				}
			},
			{
				logic: ({ eventData }) => eventData?.autoplay !== true,
				payload: { payload: { page: {} } }
			} // needs to exist to send a "all other video plays" event with the current digitaldata
		],
		'ddc.link.vehicleCompareCheck': [
			{
				payload: ({ eventData: { type, make, model, modelYear } }) => {
					const nameplate = `${make} ${model}`.toLowerCase();
					return {
						payload: {
							page: {
								pageName: `${type}:si:vls`,
								pageNameNoVehicle: `${type}:si:vls`
							},
							onclick: {
								onclick: `${type}:si:vls:content:compare`,
								onclickLinkName: `${type}:si:vls:content:action:${nameplate}`
							},
							vehicle: {
								modelYear,
								nameplate
							}
						}
					};
				}
			}
		]
	}
};
